import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Check, CheckCheck, MessageSquare, Heart, CheckCircle, Users, X, Megaphone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = '/api';

const NOTIF_ICONS = {
  comment_on_post: { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-50' },
  follower_comment: { icon: Users, color: 'text-indigo-500', bg: 'bg-indigo-50' },
  reaction_on_post: { icon: Heart, color: 'text-rose-500', bg: 'bg-rose-50' },
  post_approved: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  post_edit_approved: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  edit_approved: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  edit_rejected: { icon: X, color: 'text-red-500', bg: 'bg-red-50' },
  platform_update: { icon: Megaphone, color: 'text-purple-500', bg: 'bg-purple-50' },
  announcement: { icon: Megaphone, color: 'text-blue-600', bg: 'bg-blue-50' },
  feature: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50' },
  promotion: { icon: Heart, color: 'text-amber-500', bg: 'bg-amber-50' },
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

// Group similar notifications by type+post_id
const groupNotifications = (notifications) => {
  const groups = [];
  const seen = new Map();

  for (const notif of notifications) {
    // Group by type + post_id (only group reactions and comments)
    const groupable = ['reaction_on_post', 'comment_on_post', 'follower_comment'];
    const key = groupable.includes(notif.type) && notif.post_id
      ? `${notif.type}::${notif.post_id}`
      : null;

    if (key && seen.has(key)) {
      const group = seen.get(key);
      group.count += 1;
      group.items.push(notif);
      if (!notif.read) group.hasUnread = true;
    } else {
      const group = {
        key: key || notif.id,
        type: notif.type,
        lead: notif,
        count: 1,
        items: [notif],
        hasUnread: !notif.read,
      };
      groups.push(group);
      if (key) seen.set(key, group);
    }
  }
  return groups;
};

const groupMessage = (group) => {
  if (group.count === 1) return group.lead.message;
  const others = group.count - 1;
  if (group.type === 'reaction_on_post') {
    return `${group.lead.message.split(' reacted')[0]} and ${others} other${others > 1 ? 's' : ''} reacted to your post`;
  }
  if (group.type === 'comment_on_post') {
    return `${group.lead.message.split(' commented')[0]} and ${others} other${others > 1 ? 's' : ''} commented on your post`;
  }
  if (group.type === 'follower_comment') {
    return `${others + 1} new comments on a post you follow`;
  }
  return group.lead.message;
};

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [total, setTotal] = useState(0);
  const ref = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(res.data.unread_count || 0);
      setTotalCount(res.data.total || 0);
    } catch {}
  }, [user]);

  const fetchNotifications = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/notifications?limit=20`);
      setNotifications(res.data.notifications || []);
      setTotal(res.data.total || 0);
    } catch {}
  };

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = () => {
    if (!open) fetchNotifications();
    setOpen(!open);
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {});
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {});
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleGroupClick = (group) => {
    // Mark all unread in group as read
    group.items.forEach(n => { if (!n.read) markRead(n.id); });
    if (group.lead.link) {
      navigate(group.lead.link);
      setOpen(false);
    }
  };

  // Show bell once user has received at least one notification
  if (totalCount === 0) return null;

  const grouped = groupNotifications(notifications);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleOpen(); }}
        className={`relative p-1.5 -ml-2 rounded-lg transition-colors ${unreadCount > 0 ? 'text-[#0F2C3E] hover:bg-slate-100' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'}`}
        data-testid="notification-bell"
        aria-label="Community notifications"
        title="Community notifications"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 text-[8px] min-w-[16px] h-[16px] bg-red-500 text-white font-bold rounded-full flex items-center justify-center px-0.5" data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-3 top-20 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-96 bg-white rounded-xl shadow-xl border border-slate-100 z-50 overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-bold text-[#0F2C3E]">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[10px] text-[#D4A017] hover:underline font-medium"
                  data-testid="mark-all-read-btn"
                >
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Grouped List */}
          <div className="max-h-80 overflow-y-auto" data-testid="notification-list">
            {grouped.length === 0 ? (
              <div className="py-10 text-center">
                <Bell className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">No notifications yet</p>
                <p className="text-xs text-slate-300 mt-1">Activity on your posts will show here</p>
              </div>
            ) : (
              grouped.map(group => {
                const meta = NOTIF_ICONS[group.type] || NOTIF_ICONS.comment_on_post;
                const Icon = meta.icon;
                return (
                  <button
                    key={group.key}
                    onClick={() => handleGroupClick(group)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-50 ${group.hasUnread ? 'bg-blue-50/40' : ''}`}
                    data-testid={`notification-group-${group.key}`}
                  >
                    <div className={`relative p-1.5 rounded-full ${meta.bg} shrink-0 mt-0.5`}>
                      <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
                      {group.count > 1 && (
                        <span className="absolute -bottom-1 -right-1 text-[8px] min-w-[14px] h-[14px] bg-[#0F2C3E] text-white font-bold rounded-full flex items-center justify-center px-0.5">
                          {group.count}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {group.lead.title && ['platform_update','announcement','feature','promotion'].includes(group.type) ? (
                        <>
                          <p className={`text-xs font-semibold leading-snug ${group.hasUnread ? 'text-slate-800' : 'text-slate-600'}`}>
                            {group.lead.title}
                          </p>
                          <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">{group.lead.message}</p>
                        </>
                      ) : (
                        <p className={`text-xs leading-snug ${group.hasUnread ? 'text-slate-800 font-medium' : 'text-slate-600'}`}>
                          {groupMessage(group)}
                        </p>
                      )}
                      <p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(group.lead.created_at)}</p>
                    </div>
                    {group.hasUnread && (
                      <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-1.5" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Always show View All */}
          <div className="px-4 py-2.5 border-t border-slate-100 text-center bg-slate-50/50">
            <button
              onClick={() => { navigate('/dashboard?tab=account'); setOpen(false); }}
              className="text-xs text-[#0F2C3E] hover:text-[#D4A017] font-medium transition-colors"
              data-testid="view-all-notifications-btn"
            >
              View all notifications
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
